import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Plus, Edit2, Trash2, BookOpen, Users, Eye, EyeOff, X, Save, ChevronDown, ChevronUp } from "lucide-react";
import { useAdminAuth, formatApiErrorDetail } from "../../lib/adminAuth";

const EMPTY_BI = { tr: "", en: "" };
const blankLesson = () => ({ id: crypto.randomUUID(), title: { ...EMPTY_BI }, order: 0, duration_sec: 0, oss_key: "", resources: [] });
const blankModule = () => ({ id: crypto.randomUUID(), title: { ...EMPTY_BI }, order: 0, lessons: [blankLesson()] });
const blankCourse = () => ({
  title: { ...EMPTY_BI }, slug: "", description: { ...EMPTY_BI }, long_description: { ...EMPTY_BI },
  thumbnail: "", preview_video_oss_key: "", instructor: { ...EMPTY_BI },
  price_kurus: 0, currency: "TRY", access_days: null, extension_days: 15,
  delivery_mode: "online", venue: "", start_at: "", capacity: null,
  modules: [blankModule()], tags: [], level: "intermediate", language: "tr", status: "draft",
  _new: true,
});

export default function AdminCourses() {
  const { authedClient } = useAdminAuth();
  const [courses, setCourses] = useState([]);
  const [enrollStats, setEnrollStats] = useState({});
  const [allEnrollments, setAllEnrollments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [enrollmentsFor, setEnrollmentsFor] = useState(null); // kurs objesi

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [coursesRes, enrollRes] = await Promise.all([
        authedClient.get("/admin/courses"),
        authedClient.get("/admin/enrollments"),
      ]);
      setCourses(coursesRes.data || []);
      const items = enrollRes.data?.items || [];
      setAllEnrollments(items);
      const stats = {};
      for (const e of items) {
        if (!stats[e.course_id]) stats[e.course_id] = { total: 0, active: 0, completed: 0 };
        stats[e.course_id].total++;
        if (e.status === "active" || e.status === "extended") stats[e.course_id].active++;
        if (e.status === "completed") stats[e.course_id].completed++;
      }
      setEnrollStats(stats);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Yüklenemedi");
    } finally {
      setLoading(false);
    }
  }, [authedClient]);

  useEffect(() => { load(); }, [load]);

  const remove = async (course) => {
    if (!window.confirm(`"${course.title?.tr || course.title?.en}" kursunu silmek istiyor musunuz?`)) return;
    try {
      await authedClient.delete(`/admin/courses/${course.id}`);
      toast.success("Kurs silindi");
      load();
    } catch (e) { toast.error("Silinemedi"); }
  };

  const save = async (form) => {
    const payload = { ...form };
    delete payload._new;
    try {
      if (form._new) {
        await authedClient.post("/admin/courses", payload);
        toast.success("Kurs oluşturuldu");
      } else {
        await authedClient.put(`/admin/courses/${form.id}`, payload);
        toast.success("Kurs güncellendi");
      }
      setEditing(null);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Kaydedilemedi");
    }
  };

  return (
    <div data-testid="admin-courses-page">
      <header className="flex items-end justify-between flex-wrap gap-4 mb-7">
        <div>
          <h1 className="font-serif text-4xl md:text-5xl tracking-tight">Eğitimler</h1>
          <p className="mt-1 text-sm text-ink/60">Toplam <strong>{courses.length}</strong> kurs</p>
        </div>
        <button onClick={() => setEditing(blankCourse())} className="btn-primary">
          <Plus className="w-4 h-4" strokeWidth={1.5} /> Yeni Kurs
        </button>
      </header>

      {loading ? (
        <div className="text-ink/55 text-sm py-12 text-center">Yükleniyor…</div>
      ) : courses.length === 0 ? (
        <div className="surface-card p-10 text-center text-ink/60">
          <BookOpen className="w-8 h-8 mx-auto mb-3 text-ink/30" strokeWidth={1.4} />
          Henüz kurs yok.
        </div>
      ) : (
        <ul className="space-y-3">
          {courses.map((c) => {
            const stats = enrollStats[c.id] || {};
            return (
              <li key={c.id} className="surface-card p-5 flex flex-col md:flex-row md:items-center gap-4">
                <div className="flex items-start gap-4 flex-1 min-w-0">
                  <div className="w-16 h-16 rounded-xl overflow-hidden bg-cream-200 shrink-0">
                    {c.thumbnail
                      ? <img src={c.thumbnail} alt="" className="w-full h-full object-cover" />
                      : <div className="w-full h-full flex items-center justify-center text-cream-300"><BookOpen className="w-6 h-6" strokeWidth={1.2} /></div>
                    }
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <span className={`text-[10px] uppercase tracking-[0.18em] px-2 py-0.5 rounded-full ${c.status === "published" ? "bg-sage-100 text-sage-700" : "bg-cream-300 text-ink/60"}`}>
                        {c.status === "published" ? "Yayında" : "Taslak"}
                      </span>
                      <span className="text-xs text-ink/50">{c.slug}</span>
                    </div>
                    <p className="font-serif text-xl text-ink truncate">{c.title?.tr || c.title?.en}</p>
                    <p className="text-xs text-ink/55 mt-0.5">
                      {(c.price_kurus / 100).toLocaleString("tr-TR")} TL ·{" "}
                      {c.access_days > 0 && <>{c.access_days} gün ·{" "}</>}
                      <span className="text-sage-700">{stats.total || 0} kayıt</span>
                      {stats.completed > 0 && <span className="ml-1 text-terracotta-400">· {stats.completed} tamamlandı</span>}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2 md:flex-col md:items-stretch md:gap-1">
                  <a href={`/trainings/${c.slug}`} target="_blank" rel="noreferrer"
                    className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full border border-ink/15 text-ink/65 hover:text-ink">
                    <Eye className="w-3.5 h-3.5" strokeWidth={1.5} /> Önizle
                  </a>
                  <button onClick={() => setEnrollmentsFor(c)}
                    className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full border border-sage-300/60 text-sage-700 hover:bg-sage-100">
                    <Users className="w-3.5 h-3.5" strokeWidth={1.5} /> Kayıtlar ({enrollStats[c.id]?.total || 0})
                  </button>
                  <button onClick={() => setEditing({ ...c, _new: false })}
                    className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full border border-ink/15 text-ink/65 hover:text-ink">
                    <Edit2 className="w-3.5 h-3.5" strokeWidth={1.5} /> Düzenle
                  </button>
                  <button onClick={() => remove(c)}
                    className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full border border-terracotta-200 text-terracotta-500 hover:bg-terracotta-50">
                    <Trash2 className="w-3.5 h-3.5" strokeWidth={1.5} /> Sil
                  </button>
                </div>
              </li>
            );
          })}
        </ul>
      )}

      {editing && <CourseEditor form={editing} onChange={setEditing} onCancel={() => setEditing(null)} onSave={save} />}
      {enrollmentsFor && (
        <EnrollmentsModal
          course={enrollmentsFor}
          enrollments={allEnrollments.filter((e) => e.course_id === enrollmentsFor.id)}
          authedClient={authedClient}
          onClose={() => { setEnrollmentsFor(null); load(); }}
        />
      )}
    </div>
  );
}

const STATUS_COLORS = {
  active:    "bg-sage-100 text-sage-700",
  extended:  "bg-bluefog-200 text-sage-800",
  completed: "bg-terracotta-50 text-terracotta-500",
  expired:   "bg-cream-300 text-ink/50",
  pending:   "bg-cream-200 text-ink/60",
};
const STATUS_LABELS = {
  active: "Aktif", extended: "Uzatıldı", completed: "Tamamlandı",
  expired: "Süresi Doldu", pending: "Bekliyor",
};

function EnrollmentsModal({ course, enrollments, authedClient, onClose }) {
  const [selected, setSelected] = useState(null);
  const [extending, setExtending] = useState(false);

  const extendEnrollment = async (enrollId) => {
    if (!window.confirm("Bu kaydın süresini 15 gün uzatmak istiyor musunuz?")) return;
    setExtending(true);
    try {
      await authedClient.post(`/admin/enrollments/${enrollId}/extend`, null, { params: { days: 15 } });
      toast.success("Süre uzatıldı, email gönderildi");
      onClose();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Uzatılamadı");
    } finally {
      setExtending(false);
    }
  };

  const exportCsv = () => {
    const rows = [
      ["Ad", "Soyad", "E-posta", "Telefon", "Tutar (TL)", "Durum", "Fatura No", "Kayıt Tarihi", "Erişim Bitiş", "İlerleme %"],
      ...enrollments.map((e) => [
        e.user_name, e.user_surname, e.user_email, e.user_phone || "",
        ((e.amount_kurus || 0) / 100).toFixed(2),
        STATUS_LABELS[e.status] || e.status,
        e.invoice_number || "",
        e.created_at ? new Date(e.created_at).toLocaleDateString("tr-TR") : "",
        e.access_expires_at ? new Date(e.access_expires_at).toLocaleDateString("tr-TR") : "",
        Math.round(e.overall_percent || 0),
      ]),
    ];
    const csv = rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = `kayitlar-${course.slug}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="fixed inset-0 z-50 bg-ink/40 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => { setSelected(null); onClose(); }}>
      <div className="bg-cream-100 rounded-3xl shadow-float w-full max-w-4xl max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="sticky top-0 bg-cream-100 border-b border-cream-300/60 px-7 py-5 z-10">
          <div className="flex items-start justify-between gap-3">
            <div>
              <p className="text-xs uppercase tracking-[0.22em] text-terracotta-400 font-medium">Kayıtlar</p>
              <h2 className="font-serif text-2xl text-ink mt-1">{course.title?.tr || course.title?.en}</h2>
              <p className="text-xs text-ink/55 mt-1">
                {enrollments.length} kayıt ·{" "}
                <span className="text-sage-700">{enrollments.filter((e) => e.status === "active" || e.status === "extended").length} aktif</span>
                {" · "}
                <span className="text-terracotta-400">{enrollments.filter((e) => e.status === "completed").length} tamamlandı</span>
              </p>
            </div>
            <div className="flex items-center gap-2">
              <button onClick={exportCsv} disabled={!enrollments.length} className="text-xs px-4 py-2 rounded-full border border-ink/15 hover:bg-cream-200 disabled:opacity-40">
                CSV indir
              </button>
              <button onClick={() => { setSelected(null); onClose(); }} className="text-ink/40 hover:text-ink">
                <X className="w-5 h-5" strokeWidth={1.5} />
              </button>
            </div>
          </div>
        </div>

        {/* Liste */}
        <div className="p-7">
          {enrollments.length === 0 ? (
            <div className="text-center py-12">
              <Users className="w-8 h-8 mx-auto mb-3 text-ink/30" strokeWidth={1.4} />
              <p className="text-ink/60">Henüz kayıt yok.</p>
            </div>
          ) : (
            <ul className="space-y-2.5">
              {enrollments.map((e) => (
                <li key={e.id}>
                  <button
                    onClick={() => setSelected(selected?.id === e.id ? null : e)}
                    className="w-full text-left bg-white border border-cream-300/60 rounded-xl p-4 hover:border-sage-300 transition-colors"
                  >
                    <div className="flex items-center justify-between gap-3 flex-wrap">
                      <div className="flex items-center gap-3 min-w-0">
                        <span className={`text-[10px] uppercase tracking-[0.15em] px-2 py-0.5 rounded-full shrink-0 ${STATUS_COLORS[e.status] || "bg-cream-200 text-ink/60"}`}>
                          {STATUS_LABELS[e.status] || e.status}
                        </span>
                        <span className="font-medium text-ink truncate">{e.user_name} {e.user_surname}</span>
                        <span className="text-ink/45 text-sm truncate hidden md:inline">· {e.user_email}</span>
                      </div>
                      <div className="flex items-center gap-4 shrink-0 text-xs text-ink/55">
                        <span>%{Math.round(e.overall_percent || 0)} ilerleme</span>
                        <span>{((e.amount_kurus || 0) / 100).toLocaleString("tr-TR")} TL</span>
                      </div>
                    </div>

                    {/* Progress bar */}
                    <div className="mt-2 h-1 bg-cream-200 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-terracotta-400 rounded-full transition-all"
                        style={{ width: `${e.overall_percent || 0}%` }}
                      />
                    </div>
                  </button>

                  {/* Detay açılır */}
                  {selected?.id === e.id && (
                    <div className="bg-cream-50 border border-cream-300/60 border-t-0 rounded-b-xl px-5 py-4 space-y-3">
                      <dl className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
                        {[
                          ["E-posta", <a href={`mailto:${e.user_email}`} className="text-terracotta-400 hover:underline">{e.user_email}</a>],
                          ["Telefon", e.user_phone || "—"],
                          ["Fatura No", <span className="font-mono text-xs">{e.invoice_number || "—"}</span>],
                          ["Ödeme", `${((e.amount_kurus || 0) / 100).toLocaleString("tr-TR")} TL`],
                          ["Kayıt Tarihi", e.created_at ? new Date(e.created_at).toLocaleDateString("tr-TR") : "—"],
                          ["Erişim Bitiş", e.access_expires_at ? new Date(e.access_expires_at).toLocaleDateString("tr-TR") : "—"],
                          ["İlk Erişim", e.started_at ? new Date(e.started_at).toLocaleDateString("tr-TR") : "Henüz başlamadı"],
                          ["Tamamlanma", e.completed_at ? new Date(e.completed_at).toLocaleDateString("tr-TR") : "—"],
                          ["Uzatma Verildi", e.extension_granted ? "Evet" : "Hayır"],
                        ].map(([label, value]) => (
                          <div key={label}>
                            <dt className="text-[10px] uppercase tracking-widest text-ink/45 mb-0.5">{label}</dt>
                            <dd className="text-ink/80">{value}</dd>
                          </div>
                        ))}
                      </dl>
                      <div className="flex gap-2 pt-1">
                        {(e.status === "active" || e.status === "extended") && (
                          <button
                            onClick={() => extendEnrollment(e.id)}
                            disabled={extending}
                            className="text-xs px-4 py-2 rounded-full bg-sage-100 text-sage-700 hover:bg-sage-200 disabled:opacity-50"
                          >
                            +15 gün uzat
                          </button>
                        )}
                        <a
                          href={`mailto:${e.user_email}`}
                          className="text-xs px-4 py-2 rounded-full border border-ink/15 text-ink/65 hover:text-ink"
                        >
                          E-posta gönder
                        </a>
                      </div>
                    </div>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}

function CourseEditor({ form, onChange, onCancel, onSave }) {
  const set = (field) => (val) => onChange((p) => ({ ...p, [field]: val }));
  const setBi = (field) => (lang, v) => onChange((p) => ({ ...p, [field]: { ...p[field], [lang]: v } }));
  const [openMods, setOpenMods] = useState({});

  const addModule = () => onChange((p) => ({ ...p, modules: [...(p.modules || []), blankModule()] }));
  const removeModule = (idx) => onChange((p) => ({ ...p, modules: p.modules.filter((_, i) => i !== idx) }));
  const updateModule = (idx, field, val) => onChange((p) => {
    const mods = [...p.modules];
    mods[idx] = { ...mods[idx], [field]: val };
    return { ...p, modules: mods };
  });
  const updateModBi = (idx, field, lang, val) => onChange((p) => {
    const mods = [...p.modules];
    mods[idx] = { ...mods[idx], [field]: { ...mods[idx][field], [lang]: val } };
    return { ...p, modules: mods };
  });
  const addLesson = (mIdx) => onChange((p) => {
    const mods = [...p.modules];
    mods[mIdx] = { ...mods[mIdx], lessons: [...(mods[mIdx].lessons || []), blankLesson()] };
    return { ...p, modules: mods };
  });
  const removeLesson = (mIdx, lIdx) => onChange((p) => {
    const mods = [...p.modules];
    mods[mIdx] = { ...mods[mIdx], lessons: mods[mIdx].lessons.filter((_, i) => i !== lIdx) };
    return { ...p, modules: mods };
  });
  const updateLesson = (mIdx, lIdx, field, val) => onChange((p) => {
    const mods = [...p.modules];
    const lessons = [...mods[mIdx].lessons];
    lessons[lIdx] = { ...lessons[lIdx], [field]: val };
    mods[mIdx] = { ...mods[mIdx], lessons };
    return { ...p, modules: mods };
  });
  const updateLessonBi = (mIdx, lIdx, field, lang, val) => onChange((p) => {
    const mods = [...p.modules];
    const lessons = [...mods[mIdx].lessons];
    lessons[lIdx] = { ...lessons[lIdx], [field]: { ...lessons[lIdx][field], [lang]: val } };
    mods[mIdx] = { ...mods[mIdx], lessons };
    return { ...p, modules: mods };
  });

  return (
    <div className="fixed inset-0 z-50 bg-ink/40 backdrop-blur-sm flex items-center justify-center p-4" onClick={onCancel}>
      <div className="bg-cream-100 rounded-3xl shadow-float w-full max-w-4xl max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="sticky top-0 bg-cream-100 border-b border-cream-300/60 px-7 py-5 flex items-center justify-between z-10">
          <h2 className="font-serif text-2xl text-ink">{form._new ? "Yeni Kurs" : "Kursu Düzenle"}</h2>
          <button onClick={onCancel} className="text-ink/40 hover:text-ink"><X className="w-5 h-5" strokeWidth={1.5} /></button>
        </div>

        <div className="p-7 space-y-6">
          {/* Temel bilgiler */}
          <Bi label="Kurs Başlığı *" value={form.title} onChange={setBi("title")} />
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <span className="admin-label">Slug * (URL'de görünür)</span>
              <input value={form.slug} onChange={(e) => set("slug")(e.target.value.toLowerCase().replace(/\s+/g, "-"))} className="admin-input" placeholder="psikolojik-guvenlik" />
            </div>
            <div>
              <span className="admin-label">Durum</span>
              <select value={form.status} onChange={(e) => set("status")(e.target.value)} className="admin-input">
                <option value="draft">Taslak</option>
                <option value="published">Yayında</option>
              </select>
            </div>
          </div>
          <Bi label="Kısa Açıklama" value={form.description} onChange={setBi("description")} multiline rows={2} />
          <Bi label="Uzun Açıklama" value={form.long_description} onChange={setBi("long_description")} multiline rows={4} />
          <Bi label="Eğitmen Adı" value={form.instructor} onChange={setBi("instructor")} />

          <div>
            <span className="admin-label">Eğitim Görseli (URL)</span>
            <input value={form.thumbnail || ""} onChange={(e) => set("thumbnail")(e.target.value)} className="admin-input" placeholder="https://siteadresiniz.com/gorsel.jpg" />
            <p className="text-xs text-ink/40 mt-1">Eğitim kartında ve detay sayfasında gösterilecek görselin bağlantısını ekleyin.</p>
            {form.thumbnail && <div className="mt-3 aspect-[16/9] max-w-sm overflow-hidden rounded-xl bg-cream-200"><img src={form.thumbnail} alt="Eğitim görseli önizlemesi" className="h-full w-full object-cover" /></div>}
          </div>

          <div className="grid md:grid-cols-3 gap-4">
            <div>
              <span className="admin-label">Fiyat (kuruş) — 0 = Ücretsiz</span>
              <input type="number" min="0" value={form.price_kurus} onChange={(e) => set("price_kurus")(parseInt(e.target.value) || 0)} className="admin-input" />
              <p className="text-xs text-ink/40 mt-1">{(form.price_kurus / 100).toLocaleString("tr-TR")} TL</p>
            </div>
            <div>
              <span className="admin-label">Erişim Süresi (gün)</span>
              <input type="number" min="1" value={form.access_days || ""} onChange={(e) => set("access_days")(e.target.value ? parseInt(e.target.value, 10) : null)} className="admin-input" placeholder="Örn. 45" />
              <p className="text-xs text-ink/40 mt-1">Boş bırakırsanız ziyaretçilere süre bilgisi gösterilmez.</p>
            </div>
            <div>
              <span className="admin-label">Uzatma (gün)</span>
              <input type="number" min="0" value={form.extension_days} onChange={(e) => set("extension_days")(parseInt(e.target.value) || 0)} className="admin-input" />
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <span className="admin-label">Eğitim Türü</span>
              <select value={form.delivery_mode || "online"} onChange={(e) => set("delivery_mode")(e.target.value)} className="admin-input">
                <option value="online">Online eğitim</option>
                <option value="offline">Yüz yüze eğitim</option>
              </select>
            </div>
            {form.delivery_mode === "offline" && (
              <div>
                <span className="admin-label">Kontenjan</span>
                <input type="number" min="1" value={form.capacity || ""} onChange={(e) => set("capacity")(e.target.value ? parseInt(e.target.value, 10) : null)} className="admin-input" placeholder="Örn. 10" />
              </div>
            )}
          </div>
          {form.delivery_mode === "offline" && (
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <span className="admin-label">Mekan</span>
                <input value={form.venue || ""} onChange={(e) => set("venue")(e.target.value)} className="admin-input" placeholder="Örn. X Mekan, İstanbul" />
              </div>
              <div>
                <span className="admin-label">Tarih ve saat</span>
                <input type="datetime-local" value={form.start_at || ""} onChange={(e) => set("start_at")(e.target.value)} className="admin-input" />
              </div>
            </div>
          )}

          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <span className="admin-label">Seviye</span>
              <select value={form.level || "intermediate"} onChange={(e) => set("level")(e.target.value)} className="admin-input">
                <option value="beginner">Başlangıç</option>
                <option value="intermediate">Orta Düzey</option>
                <option value="advanced">İleri</option>
              </select>
            </div>
            <div>
              <span className="admin-label">Etiketler (virgülle ayırın)</span>
              <input
                value={(form.tags || []).join(", ")}
                onChange={(e) => set("tags")(e.target.value.split(",").map((t) => t.trim()).filter(Boolean))}
                className="admin-input"
                placeholder="liderlik, refah, psikoloji"
              />
            </div>
          </div>

          {/* Modüller */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <span className="font-serif text-xl text-ink">Modüller & Dersler</span>
              <button onClick={addModule} className="text-xs uppercase tracking-[0.18em] text-terracotta-400 hover:text-terracotta-500">+ Modül Ekle</button>
            </div>
            <div className="space-y-3">
              {(form.modules || []).map((m, mi) => (
                <div key={m.id} className="border border-cream-300/60 rounded-xl overflow-hidden">
                  <div className="flex items-center gap-3 px-4 py-3 bg-cream-50">
                    <button onClick={() => setOpenMods((p) => ({ ...p, [m.id]: !p[m.id] }))} className="text-ink/60">
                      {openMods[m.id] ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                    </button>
                    <div className="flex-1 grid grid-cols-2 gap-2">
                      <input value={m.title?.tr || ""} onChange={(e) => updateModBi(mi, "title", "tr", e.target.value)} className="admin-input text-sm" placeholder="Modül adı (TR)" />
                      <input value={m.title?.en || ""} onChange={(e) => updateModBi(mi, "title", "en", e.target.value)} className="admin-input text-sm" placeholder="Module name (EN)" />
                    </div>
                    <button onClick={() => removeModule(mi)} className="text-ink/30 hover:text-terracotta-400">
                      <X className="w-4 h-4" strokeWidth={1.5} />
                    </button>
                  </div>
                  {openMods[m.id] && (
                    <div className="p-4 space-y-2">
                      <div className="grid grid-cols-[1fr_1fr_140px_100px_32px] gap-2 items-center text-[10px] uppercase tracking-widest text-ink/40 px-1">
                        <span>Ders Adı (TR)</span>
                        <span>Lesson Title (EN)</span>
                        <span>OSS Key</span>
                        <span>Süre (dk)</span>
                        <span></span>
                      </div>
                      {(m.lessons || []).map((l, li) => (
                        <div key={l.id} className="grid grid-cols-[1fr_1fr_140px_100px_32px] gap-2 items-center">
                          <input value={l.title?.tr || ""} onChange={(e) => updateLessonBi(mi, li, "title", "tr", e.target.value)} className="admin-input text-sm" placeholder="Ders adı (TR)" />
                          <input value={l.title?.en || ""} onChange={(e) => updateLessonBi(mi, li, "title", "en", e.target.value)} className="admin-input text-sm" placeholder="Lesson title (EN)" />
                          <input value={l.oss_key || ""} onChange={(e) => updateLesson(mi, li, "oss_key", e.target.value)} className="admin-input text-sm" placeholder="ornek/video.mp4" />
                          <div className="relative">
                            <input
                              type="number"
                              min="0"
                              value={l.duration_sec > 0 ? Math.round(l.duration_sec / 60) : ""}
                              onChange={(e) => updateLesson(mi, li, "duration_sec", (parseInt(e.target.value) || 0) * 60)}
                              className="admin-input text-sm pr-7"
                              placeholder="45"
                            />
                            <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-[10px] text-ink/40">dk</span>
                          </div>
                          <button onClick={() => removeLesson(mi, li)} className="text-ink/30 hover:text-terracotta-400 flex items-center justify-center">
                            <X className="w-3.5 h-3.5" strokeWidth={1.5} />
                          </button>
                        </div>
                      ))}
                      <button onClick={() => addLesson(mi)} className="text-xs text-terracotta-400 hover:text-terracotta-500 mt-1">+ Ders Ekle</button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="sticky bottom-0 bg-cream-100 border-t border-cream-300/60 px-7 py-4 flex justify-end gap-2">
          <button onClick={onCancel} className="btn-secondary">İptal</button>
          <button onClick={() => onSave(form)} className="btn-primary">
            <Save className="w-4 h-4" strokeWidth={1.5} /> Kaydet
          </button>
        </div>
      </div>

      <style>{`
        .admin-input { width:100%;background:#FBFAF6;border:1px solid #E5E0D8;border-radius:0.75rem;padding:0.65rem 0.9rem;font-family:'Outfit',sans-serif;font-size:0.92rem;color:#2C3531;font-weight:300;transition:all 0.2s; }
        .admin-input:focus { outline:none;border-color:#839788;background:#fff;box-shadow:0 0 0 3px rgba(131,151,136,0.12); }
        .admin-label { display:block;font-size:11px;text-transform:uppercase;letter-spacing:0.18em;color:rgba(44,53,49,0.55);margin-bottom:6px;font-weight:500; }
      `}</style>
    </div>
  );
}

function Bi({ label, value, onChange, multiline = false, rows = 2 }) {
  return (
    <div>
      <span className="admin-label">{label}</span>
      <div className="grid md:grid-cols-2 gap-3">
        {["tr", "en"].map((l) => (
          <div key={l}>
            <span className="block text-[10px] uppercase tracking-widest text-ink/45 mb-1">{l.toUpperCase()}</span>
            {multiline
              ? <textarea rows={rows} value={value?.[l] || ""} onChange={(e) => onChange(l, e.target.value)} className="admin-textarea" />
              : <input value={value?.[l] || ""} onChange={(e) => onChange(l, e.target.value)} className="admin-input" />
            }
          </div>
        ))}
      </div>
    </div>
  );
}
